import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const RECENT_SEARCH_STORAGE_KEY = 'cnstrc-predictive-search-recent';
const RECENT_SEARCH_MAX = 5;

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput
 * @property {HTMLButtonElement} [resetButton] - The clear/reset button.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback?.();
    this.#updateResetButtonVisibility();
    const input = this.refs.searchPageInput;
    if (input) input.addEventListener('input', this.#updateResetButtonVisibility);
    input?.form?.addEventListener('submit', this.#handleSearchSubmit);
  }

  disconnectedCallback() {
    const input = this.refs?.searchPageInput;
    if (input) input.removeEventListener('input', this.#updateResetButtonVisibility);
    input?.form?.removeEventListener('submit', this.#handleSearchSubmit);
    super.disconnectedCallback?.();
  }

  #updateResetButtonVisibility = () => {
    const input = this.refs?.searchPageInput;
    const resetButton = this.refs?.resetButton;
    if (resetButton) resetButton.hidden = !input?.value?.trim();
  };

  /** Clears the search input only; does not submit the form. */
  clearSearchOnly = () => {
    const input = this.refs.searchPageInput;
    if (!input) return;
    input.value = '';
    this.#updateResetButtonVisibility();
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
  };

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };

  #handleSearchSubmit = () => {
    this.#addToRecentSearches(this.refs.searchPageInput?.value || '');
  };

  /**
   * @returns {string[]}
   */
  #getRecentSearches() {
    try {
      const raw = localStorage.getItem(RECENT_SEARCH_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      const seen = new Set();
      return parsed
        .filter((item) => typeof item === 'string')
        .map((item) => item.trim())
        .filter((item) => {
          if (!item || seen.has(item)) return false;
          seen.add(item);
          return true;
        })
        .slice(0, RECENT_SEARCH_MAX);
    } catch {
      return [];
    }
  }

  /**
   * @param {string[]} terms
   */
  #setRecentSearches(terms) {
    try {
      localStorage.setItem(RECENT_SEARCH_STORAGE_KEY, JSON.stringify(terms));
    } catch {
      // localStorage may be unavailable
    }
  }

  /**
   * @param {string} term
   */
  #addToRecentSearches(term) {
    const trimmed = term.trim();
    if (!trimmed) return;

    const recentSearches = this.#getRecentSearches();
    const next = [trimmed, ...recentSearches.filter((item) => item !== trimmed)].slice(
      0,
      RECENT_SEARCH_MAX,
    );
    this.#setRecentSearches(next);
  }
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
